import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Chip, Button } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useDemoRole } from '../../../../contexts/DemoRoleContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUserOrganizations } from '../../hooks/useOrganization';
import OrgSwitcher from '../../components/OrgSwitcher';
import DataInputsStatus from '../../components/DataInputsStatus';
import RoleHero from './RoleHero';

/**
 * Org IT Portal — Mindmap branch 1, "Org IT" persona
 *   "Interfaces with Org Software/Databases"
 *
 * IT-facing status view of the data inputs feeding REPSit.ai for the active
 * organization — live record counts for connected sources, plus the client
 * systems that connect during integration.
 */
function OrgITPortal() {
  const navigate = useNavigate();
  const { persona, personaUser } = useDemoRole();
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const activeOrg = organizations[0] || null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <OrgSwitcher />
      </Box>
      <RoleHero
        icon={StorageIcon}
        title="Org IT Portal"
        persona={persona}
        personaUser={personaUser}
        color="#37474f"
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Monitor the data inputs feeding {activeOrg?.name || 'your organization'} — all data is
          encrypted in transit and at rest.
        </Typography>
      </RoleHero>

      <DataInputsStatus organizationId={activeOrg?.id || null} />

      {/* Public API — moved here from the REPSit dashboard's "Coming" row (⚑Q-10 adopted, P1-09): not an IA, an IT integration */}
      <Card variant="outlined" sx={{ mt: 3, maxWidth: 640 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CodeIcon color="action" />
            <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
              Public API &amp; Integrations
            </Typography>
            <Chip icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />} label="Planned" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Scores, warnings, and webhooks in your own systems, with API keys you create and scope. Preview the plan in the API Docs.
          </Typography>
          <Button size="small" sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700 }} onClick={() => navigate('/api-docs')} endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}>
            Preview API Docs
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

export default OrgITPortal;
